import type { FC } from 'react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import Image from '@atlaskit/image';
import { Box, Text, xcss } from '@atlaskit/primitives';

import { ANALYTICS_UI_SUBJECT_ID, type AnalyticsOrigin } from '../constants/snippetAnalytics.ts';
import { EMPTY_STATE_VARIANT } from '../constants/emptyStateVariant.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';

import EmptyFavoritesImg from './img/EmptyFavoritesImg.svg';
import EmptyMyTemplatesImg from './img/EmptyMyTemplatesImg.svg';
import EmptySearchDarkImg from './img/EmptySearchDark.svg';
import EmptySearchLightImg from './img/EmptySearchLight.svg';
import { i18n } from './EmptySnippets.i18n.ts';

const wrapperStyle = xcss({
	paddingTop: 'space.800',
	marginBottom: 'space.100',
});

export type EmptySnippetsProps = {
	/** Origin for analytics (e.g. SNIPPET_PANEL, SNIPPET_GALLERY). Primary action clicks are reported with this origin. */
	origin: AnalyticsOrigin;
	/** Which empty state to show. Parent should derive this from selectedCategoryId and whether the category is empty (before search/filter). */
	variant: EMPTY_STATE_VARIANT;
	/** Called when the user clicks the primary action (e.g. "See how" for my-templates, "Explore templates" for favorites). Omit for search variant. */
	primaryAction?: () => void;
};

export const EmptySnippets: FC<EmptySnippetsProps> = ({ origin, variant, primaryAction }) => {
	const intl = useIntl();
	const { fireButtonClickedAnalytic } = useSnippetAnalytics(origin);

	let header: string;
	let description: React.ReactNode;
	let imageUrl: string | undefined;
	let imageHeight: number;
	let renderImage: (() => React.ReactNode) | undefined;
	let primaryActionLabel: string | null = null;

	if (variant === EMPTY_STATE_VARIANT.MY_TEMPLATES) {
		header = intl.formatMessage(i18n.myTemplatesEmptyHeader);
		description = (
			<FormattedMessage
				{...i18n.myTemplatesEmptyDescription}
				values={{
					categoryName: (chunks: React.ReactNode[]) => <Text as="strong">{chunks}</Text>,
					br: <br />,
				}}
			/>
		);
		imageUrl = EmptyMyTemplatesImg;
		imageHeight = 165;
		if (primaryAction) {
			primaryActionLabel = intl.formatMessage(i18n.myTemplatesEmptyPrimaryAction);
		}
	} else if (variant === EMPTY_STATE_VARIANT.FAVORITES) {
		header = intl.formatMessage(i18n.favoritesEmptyHeader);
		description = intl.formatMessage(i18n.favoritesEmptyDescription);
		imageUrl = EmptyFavoritesImg;
		imageHeight = 165;
		if (primaryAction) {
			primaryActionLabel = intl.formatMessage(i18n.favoritesEmptyPrimaryAction);
		}
	} else {
		header = intl.formatMessage(i18n.emptyHeader);
		description = intl.formatMessage(i18n.emptyDescription);
		imageHeight = 160;
		renderImage = () => <Image src={EmptySearchLightImg} srcDark={EmptySearchDarkImg} alt="" />;
	}

	const handlePrimaryAction = () => {
		if (variant === EMPTY_STATE_VARIANT.MY_TEMPLATES) {
			fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.EMPTY_MY_TEMPLATES_CALL_TO_ACTION);
		} else if (variant === EMPTY_STATE_VARIANT.FAVORITES) {
			fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.EMPTY_FAVORITES_CALL_TO_ACTION);
		}
		primaryAction?.();
	};

	const primaryActionButton: React.ReactNode =
		primaryAction && primaryActionLabel ? (
			<Button appearance="primary" onClick={handlePrimaryAction}>
				{primaryActionLabel}
			</Button>
		) : null;

	return (
		<Box xcss={wrapperStyle}>
			<EmptyState
				header={header}
				description={description}
				imageUrl={imageUrl}
				imageHeight={imageHeight}
				renderImage={renderImage}
				primaryAction={primaryActionButton ?? undefined}
			/>
		</Box>
	);
};
